/*
Belama — Quote Cart System
Phase 2.4 (Stabilized)
------------------------
Purpose:
- Maintain a local quote cart in localStorage
- Handle Add to Quote, Quantity +/- , Remove
- Render both bubble counter and quote page table
- Compatible with Klaviyo F3 bridge (single push)
*/

class QuoteCart {
  constructor(opts = {}) {
    this.key = opts.key || 'quoteCart';
    this.items = [];
    this.load();

    // Bind context-safe methods
    this.renderBubble = this.renderBubble.bind(this);
    this.renderPageTable = this.renderPageTable.bind(this);
    this.broadcastUpdate = this.broadcastUpdate.bind(this);
    this.bindEvents = this.bindEvents.bind(this);

    // Expose globally
    window.quoteCart = this;

    // Wait for DOM
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', this.init.bind(this));
    } else {
      this.init();
    }
  }

  // ------------------------------
  // Initialization
  // ------------------------------
  init() {
    console.log('[QuoteCart] ✅ Initialized and DOM ready');
    this.bindEvents();
    this.renderBubble();
    this.renderPageTable();
    this.broadcastUpdate();
  }

  // ------------------------------
  // LocalStorage helpers
  // ------------------------------
  load() {
    try {
      const stored = localStorage.getItem(this.key);
      this.items = stored ? JSON.parse(stored) : [];
    } catch (err) {
      console.warn('⚠️ Corrupt localStorage payload, resetting', err);
      this.items = [];
    }
  }

  save() {
    localStorage.setItem(this.key, JSON.stringify(this.items));
    this.renderBubble();
    this.broadcastUpdate();
  }

  // ------------------------------
  // CRUD
  // ------------------------------
  _normalizeItem(i) {
    return {
      productId: String(i.productId || ''),
      variantId: String(i.variantId || ''),
      title: i.title || '',
      qty: parseInt(i.qty || 1, 10),
      image: i.image || ''
    };
  }

  addItem({ productId, variantId, title, qty = 1, image = '' }) {
    const v = String(variantId);
    const existing = this.items.find(it => it.variantId === v);
    if (existing) {
      existing.qty = Math.max(1, existing.qty + Number(qty || 1));
    } else {
      this.items.push(this._normalizeItem({ productId, variantId: v, title, qty, image }));
    }
    this.save();
  }

  updateQuantity(variantId, qty) {
    const item = this.items.find(i => i.variantId === String(variantId));
    if (item) {
      item.qty = Math.max(1, Number(qty));
      this.save();
    }
  }

  removeItem(variantId) {
    this.items = this.items.filter(i => i.variantId !== String(variantId));
    this.save();
    this.renderPageTable();
  }

  clear() {
    this.items = [];
    this.save();
    this.renderPageTable();
  }

  getCount() {
    return this.items.reduce((sum, i) => sum + (i.qty || 0), 0);
  }

  // ------------------------------
  // UI Renders
  // ------------------------------
  renderBubble() {
    const bubble = document.querySelector('#cart-icon-bubble .cart-count-bubble');
    if (!bubble) return;
    const count = this.getCount();
    if (count > 0) {
      bubble.innerHTML = `<span aria-hidden="true">${count}</span>`;
      bubble.classList.remove('hidden');
    } else {
      bubble.innerHTML = '';
      bubble.classList.add('hidden');
    }
  }

  renderPageTable() {
    const table = document.getElementById('quote-cart-table');
    const empty = document.getElementById('quote-cart-empty');
    const tbody = document.getElementById('quote-cart-items');
    if (!table || !tbody) return;

    tbody.innerHTML = '';

    if (!this.items.length) {
      table.style.display = 'none';
      if (empty) empty.style.display = 'block';
      return;
    }

    table.style.display = 'block';
    if (empty) empty.style.display = 'none';

    this.items.forEach(item => {
      const safeTitle = this._escape(item.title);
      const imageUrl = this._sanitizeImage(item.image);
      const variantId = item.variantId;

      const row = document.createElement('tr');
      row.className = 'quote-cart__row';
      row.dataset.variantId = variantId;

      row.innerHTML = `
        <td class="quote-item__product">
          <div class="quote-item__product-inner" style="display:flex;align-items:center;gap:1rem;">
            <img src="${imageUrl}" alt="${safeTitle}" class="quote-item__img"
                 style="width:80px;height:80px;object-fit:contain;border-radius:8px" loading="lazy">
            <div class="quote-item__title">${safeTitle}</div>
          </div>
        </td>
        <td class="quote-item__actions" style="text-align:right;">
          <div class="quantity" style="display:inline-flex;align-items:center;border:1px solid #ddd;border-radius:6px;overflow:hidden;">
            <button type="button" class="quantity__button decrease" aria-label="Disminuir cantidad">−</button>
            <input type="number" class="quantity__input" value="${item.qty}" min="1" data-variant-id="${variantId}"
                   style="width:64px;text-align:center;border:none;">
            <button type="button" class="quantity__button increase" aria-label="Aumentar cantidad">+</button>
          </div>
          <button class="remove-quote-item" data-variant-id="${variantId}"
                  aria-label="Eliminar"
                  style="margin-left:8px;background:none;border:none;font-size:1.1rem;cursor:pointer;">🗑️</button>
        </td>
      `;

      tbody.appendChild(row);
    });
  }

  // ------------------------------
  // Utilities
  // ------------------------------
  _escape(str) {
    return String(str || '').replace(/[&<>"']/g, s => (
      { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[s]
    ));
  }

  _sanitizeImage(url) {
    if (!url) return '//placehold.co/80x80?text=No+Image';
    let u = url.trim();
    if (u.startsWith('https:files') || u.startsWith('http:files')) {
      u = u.replace(/^https?:files/, '//belama.cl/cdn/shop/files/');
    }
    if (u.startsWith('/cdn/shop/files')) {
      u = '//belama.cl' + u;
    }
    if (!u.startsWith('http') && !u.startsWith('//')) {
      u = '//belama.cl/cdn/shop/files/' + u;
    }
    return u;
  }

  broadcastUpdate() {
    document.dispatchEvent(new CustomEvent('quote:updated', { detail: { items: this.items } }));
  }


  
  // ------------------------------
// Event Binding (replace entirely)
// ------------------------------
bindEvents() {
  if (window.__quoteCartBound) return;
  window.__quoteCartBound = true;

  // Detect if a quantity control lives in the cart context (table/rows),
  // not in product/collection cards.
  const isInCartContext = (el) =>
    !!(el.closest('#quote-cart-table') || el.closest('.quote-cart__row') || el.closest('.quote-cart'));

  document.addEventListener('click', (e) => {
    // Add to Quote (outside or inside — always allowed)
    const addBtn = e.target.closest('.add-to-quote');
    if (addBtn) {
      e.preventDefault();

      const productId = addBtn.dataset.productId;
      const variantId = addBtn.dataset.variantId;
      const title = addBtn.dataset.title;
      let image = addBtn.dataset.image || '';

      // Use local qty (outside cart this is just "how many more")
      const container = addBtn.closest('.quote-actions');
      const qtyInput = container ? container.querySelector('.quantity__input') : null;
      const qty = qtyInput ? Math.max(1, parseInt(qtyInput.value || 1, 10)) : 1;

      this.addItem({ productId, variantId, title, qty, image });
      return;
    }

    // Quantity buttons (+/-)
    const minusBtn = e.target.closest('.quantity__button.decrease');
    const plusBtn  = e.target.closest('.quantity__button.increase');
    if (minusBtn || plusBtn) {
      const btn = minusBtn || plusBtn;
      const qtyBox = btn.closest('.quantity');
      const input  = qtyBox && qtyBox.querySelector('.quantity__input');
      if (!qtyBox || !input) return;

      const current = Math.max(1, parseInt(input.value || 1, 10));
      const next = Math.max(1, current + (plusBtn ? 1 : -1));
      input.value = next;

      // Only persist to localStorage when the control is inside the cart UI
      if (isInCartContext(qtyBox)) {
        const variantId = input.dataset.variantId;
        if (variantId) {
          this.updateQuantity(variantId, next);
          this.renderPageTable();
        }
      }
      return;
    }

    // Remove item (cart only)
    const remBtn = e.target.closest('.remove-quote-item');
    if (remBtn) {
      const variantId = remBtn.dataset.variantId;
      if (variantId) this.removeItem(variantId);
      return;
    }
  });

  // Manual quantity edits (only persist when inside cart)
  document.addEventListener('change', (e) => {
    if (!e.target.matches('.quantity__input')) return;

    const input = e.target;
    const next  = Math.max(1, parseInt(input.value || 1, 10));
    input.value = next;

    if (isInCartContext(input)) {
      const variantId = input.dataset.variantId;
      if (variantId) {
        this.updateQuantity(variantId, next);
        this.renderPageTable();
      }
    }
  });

  // Quote form submit
  document.addEventListener('submit', (e) => {
    const form = e.target.closest('#quote-request-form');
    if (!form) return;

    e.preventDefault();
    const email = form.querySelector('#quote-email')?.value || '';
    const note  = form.querySelector('#quote-note')?.value  || '';
    const payload = { email, note, items: this.items };

    console.log('QUOTE SUBMIT payload (to be wired):', payload);
    alert('Tu solicitud de cotización ha sido enviada (demo).');
    this.clear();
  });
}

}

// ============================================================
//  Email Confirmation Validator — F5 Feature 19
//  Purpose: Ensure both email fields match before submission.
//  UX: Shows warning + blocks submission until corrected.
// ============================================================
(() => {
  const form = document.querySelector("#quote-request-form, .quote-cart-form");
  if (!form) return;

  const email = form.querySelector("#quote-email");
  const emailConfirm = form.querySelector("#quote-email-confirm");
  const warning = form.querySelector("#email-warning");

  if (!email || !emailConfirm || !warning) return;

  // Helper: check emails and update UI
  function validateEmails(showImmediate = false) {
    const match =
      email.value.trim().toLowerCase() ===
      emailConfirm.value.trim().toLowerCase();

    warning.hidden = match;
    form.dataset.emailValid = match ? "true" : "false";

    // Optional live feedback (turns border red)
    if (showImmediate) {
      [email, emailConfirm].forEach((el) => {
        el.style.borderColor = match ? "" : "#d00";
      });
    }
  }

  // Live validation on input
  email.addEventListener("input", () => validateEmails(true));
  emailConfirm.addEventListener("input", () => validateEmails(true));

  // Hard stop on submit
  form.addEventListener("submit", (e) => {
    validateEmails(true);
    const valid = form.dataset.emailValid === "true";

    if (!valid) {
      e.preventDefault();
      e.stopImmediatePropagation(); // stop any other handlers (e.g., bridge)
      warning.hidden = false;
      emailConfirm.focus();
      console.warn("[F5] ❌ Email mismatch — submission blocked.");
    }
  });
})();

// ============================================================
//  F5 Quote Submission UI Feedback
// ============================================================
window.addEventListener("quote:submitted", () => {
  console.log("[QuoteCart] ✅ Quote submitted — clearing UI");

  const cartWrapper = document.querySelector(".quote-cart__layout");
  const form = document.querySelector("#quote-request-form");
  const submitBtn = document.querySelector("[data-quote-submit]");
  const emptyDiv = document.getElementById("quote-cart-empty");
  const resultBox = document.getElementById("quote-result");

  // Fade out cart + form
  cartWrapper?.classList.add("quote-fade-out");

  // After fade, clear content
  setTimeout(() => {
    localStorage.removeItem("quoteCart");
    if (cartWrapper) cartWrapper.style.display = "none";
    if (emptyDiv) emptyDiv.style.display = "block";

    if (resultBox) {
      resultBox.innerHTML = `
        <div class="quote-success-message">
          <h2>✅ Cotización enviada correctamente</h2>
          <p>Hemos recibido su solicitud. Nuestro equipo la revisará y le contactará a la brevedad.</p>
          <a href="/" class="button button--primary">Volver a la tienda</a>
        </div>
      `;
      resultBox.style.display = "block";
      resultBox.classList.add("quote-fade-in");
    }

    if (submitBtn) {
      submitBtn.disabled = true;
      submitBtn.textContent = "Enviado";
    }
  }, 400);
});


// ------------------------------
// Initialize
// ------------------------------
if (!window.quoteCart) {
  window.quoteCart = new QuoteCart();
}



/*# Quote Cart — Future Features Backlog

## UI / UX
- Replace popup with a slide-in drawer showing quote contents.
- Allow merchant to configure popup background color, duration, and placement.
- Add mini cart “view quote” link inside popup.

## Cart Functionality
- Support per-variant options (color/size).
- Sync localStorage quote cart with customer account if logged in.
- 🚧 Opportunity for a diverged buy/checkout flow regarding current flow: QUOTE CART
  Buy/checkout flow currently postponed — only quote flow is implemented.

## Product Page
- Replace redirect `'/'` with configurable return (homepage, collection, or referrer).
- Support dynamic variant switching (update dataset on buttons).

## Admin
- Add translations to more languages.
- Configurable settings via theme editor (popup toggle, redirect, etc).
*/
